import { passiveAuth, forceAuth } from '@xfinity/maw-fetch';
import Cookies from 'js-cookie';

import config from '../config';
import apiClient from '../middleware/apiClient';
import { getSession } from './session';
import {
  SKIP_PASSIVE_AUTH_SESSIONS,
  ALLOWED_AUTH_SESSIONS,
  forceLoginV2,
  passiveLoginV2,
  forceLoginV1,
  passiveLoginV1,
  isTargetAuthEnabled,
  isHintCookieAvailable,
} from '../helpers/auth';
import {
  getAccountIndex,
  setAccountIndex,
  setCspAccountToken,
  getAccountPerms,
  getAccountPermsFromUserApi,
} from '../helpers/account';
import {
  MACAROON, CONSENT, SSM_USER, SSM_XM_CUSTOMER_ACCOUNT,
} from '../helpers/apis';
import {
  COTTON_MACAROON, COTTON_SSM_USER, COTTON_CONSENT, COTTON_SSM_XM_CUSTOMER_ACCOUNT,
} from '../helpers/cottonApis';
import { flagEnabled } from '../helpers/featureFlags';
import { whenFlagsReady } from '../helpers/featureFlags/FirebaseFeatureFlags';
import { MOBILE_MAINTENANCE_ERROR } from '../helpers/routes';
import { setGuid, kibanaLog } from '../helpers/logger';
import { isNowPage } from '../helpers/now';
import isMobilePage from '../helpers/mobile';
import { dispatchGetUserFailure, dispatchGetUserSuccess } from './user';
import cottonFlagManager, { isCottonSession as getCottonSession } from '../helpers/featureFlags/cotton';

// Fetch Macaroon
export const GET_AUTH = 'GET_AUTH';
export const GET_AUTH_SUCCESS = 'GET_AUTH_SUCCESS';
export const GET_AUTH_FAILURE = 'GET_AUTH_FAILURE';

// Fetch Macaroon (new auth flow)
export const GET_MACAROON = 'GET_MACAROON';
export const GET_MACAROON_SUCCESS = 'GET_MACAROON_SUCCESS';
export const GET_MACAROON_FAILURE = 'GET_MACAROON_FAILURE';

export const SET_AUTH_STATUS = 'SET_AUTH_STATUS';
export const SET_FORCE_AUTHENTICATION = 'SET_FORCE_AUTHENTICATION';
export const SET_PERMS_ERROR = 'SET_PERMS_ERROR';
export const SET_ACCOUNT_PERMS = 'SET_ACCOUNT_PERMS';
export const SET_ACCOUNT_INDEX = 'SET_ACCOUNT_INDEX';

// Fetch User (SSM User API — replaces Macaroon)
export const GET_USER_AUTH = 'GET_USER_AUTH';
export const GET_USER_AUTH_SUCCESS = 'GET_USER_AUTH_SUCCESS';
export const GET_USER_AUTH_FAILURE = 'GET_USER_AUTH_FAILURE';

// Safe Connections check
export const CHECK_SAFE_CONNECTIONS = 'CHECK_SAFE_CONNECTIONS';
export const CHECK_SAFE_CONNECTIONS_SUCCESS = 'CHECK_SAFE_CONNECTIONS_SUCCESS';

function dispatchGetAuth(device = '', crsId = null, showLoading = true) {
  // Handles alternative authentication via consent new user flow (i.e., ?crsId=...)
  if (crsId) {
    const resolveEndpoint = getCottonSession()
      ? COTTON_CONSENT.RESOLVE(crsId)
      : CONSENT.RESOLVE(crsId);
    return {
      type: GET_AUTH,
      variant: 'CRS',
      payload: apiClient.fetch(resolveEndpoint),
      showLoading,
    };
  }

  // TODO: Remove linkedAccounts query param when removed from MW.
  // The linkedAccounts query param indicates to MW that FE is ready for new linked accounts
  let queryParam = '?linkedAccounts=true';
  if (device === 'ios' || device === 'android') {
    queryParam += `&device=${device}`;
  }
  return {
    type: GET_AUTH,
    payload: apiClient.fetch((getCottonSession() ? COTTON_MACAROON : MACAROON) + queryParam),
    showLoading,
  };
}

function dispatchSetAccountPerms(payload) {
  return {
    type: SET_ACCOUNT_PERMS,
    payload,
  };
}

function dispatchGetAuthSuccess(payload) {
  return {
    type: GET_AUTH_SUCCESS,
    payload,
  };
}

function dispatchGetAuthFailure(error) {
  return {
    type: GET_AUTH_FAILURE,
    payload: error,
  };
}

function dispatchGetMacaroon(device = '', crsId = null, showLoading = true) {
  if (crsId) {
    const resolveEndpoint = getCottonSession()
      ? COTTON_CONSENT.RESOLVE(crsId)
      : CONSENT.RESOLVE(crsId);
    return {
      type: GET_MACAROON,
      variant: 'CRS',
      payload: apiClient.fetch(resolveEndpoint),
      showLoading,
    };
  }

  let queryParam = '?linkedAccounts=true';
  if (device === 'ios' || device === 'android') {
    queryParam += `&device=${device}`;
  }
  return {
    type: GET_MACAROON,
    payload: apiClient.fetch((getCottonSession() ? COTTON_MACAROON : MACAROON) + queryParam),
    showLoading,
  };
}

function dispatchGetMacaroonSuccess(payload) {
  return {
    type: GET_MACAROON_SUCCESS,
    payload,
  };
}

function dispatchGetMacaroonFailure(error) {
  return {
    type: GET_MACAROON_FAILURE,
    payload: error,
  };
}

function dispatchGetUserAuth(crsId = null, showLoading = true, useCotton = false) {
  if (crsId) {
    const resolveEndpoint = useCotton
      ? COTTON_CONSENT.RESOLVE(crsId)
      : CONSENT.RESOLVE(crsId);
    return {
      type: GET_USER_AUTH,
      variant: 'CRS',
      payload: apiClient.fetch(resolveEndpoint),
      showLoading,
    };
  }

  const userEndpoint = useCotton ? COTTON_SSM_USER : SSM_USER;
  return {
    type: GET_USER_AUTH,
    payload: apiClient.fetch(userEndpoint),
    showLoading,
  };
}

function dispatchGetUserAuthSuccess() {
  return {
    type: GET_USER_AUTH_SUCCESS,
  };
}

function dispatchGetUserAuthFailure(error) {
  return {
    type: GET_USER_AUTH_FAILURE,
    payload: error,
  };
}

function dispatchSetAuthStatus(payload) {
  return {
    type: SET_AUTH_STATUS,
    payload,
  };
}

function dispatchSetForceAuthentication() {
  return {
    type: SET_FORCE_AUTHENTICATION,
  };
}

function dispatchSetPermsError() {
  return {
    type: SET_PERMS_ERROR,
  };
}

export const setAuthStatus = status => dispatch => (
  dispatch(dispatchSetAuthStatus(status))
);

function dispatchSetAccountIndex(payload) {
  return {
    type: SET_ACCOUNT_INDEX,
    payload,
  };
}

function dispatchCheckSafeConnections(isCottonSession = false) {
  const endpoint = isCottonSession ? COTTON_SSM_XM_CUSTOMER_ACCOUNT : SSM_XM_CUSTOMER_ACCOUNT;
  return {
    type: CHECK_SAFE_CONNECTIONS,
    payload: apiClient.fetch(endpoint),
  };
}

function dispatchCheckSafeConnectionsSuccess(payload) {
  return {
    type: CHECK_SAFE_CONNECTIONS_SUCCESS,
    payload,
  };
}


// Dispatches SET_PERMS_ERROR if the account lacks a flow-authorized role:
// PRIMARY or MANAGER when isUserAPIInvoked is true, otherwise PRIMARY or BILLPAY.
// Now pages additionally require the PRIMARY role.
const dispatchPermsErrorIfUnauthorized = (dispatch, accountPerms, isUserAPIInvoked) => {
  const authorizedRoles = isUserAPIInvoked ? ['PRIMARY', 'MANAGER'] : ['PRIMARY', 'BILLPAY'];
  const hasAuthorizedRole = accountPerms.some(item => authorizedRoles.includes(item));
  const isPrimary = accountPerms.includes('PRIMARY');
  if (!hasAuthorizedRole || (isNowPage(window.location.pathname) && !isPrimary)) {
    dispatch(dispatchSetPermsError());
  }
};

// Helper wrapped around passiveAuth with error handling
const getPassiveAuth = async (options) => {
  await passiveAuth(options);
  if (Cookies.get('isAuth') === '1') return;

  // If the isAuth cookie isn't set correctly, then something is wrong.
  // Force auth in this case.
  forceAuth(options);

  const authError = new Error('Invalid auth cookie');
  authError.forceAuthentication = true;
  throw authError;
};

// Dispatches the appropriate auth action based on the replaceMacaroonApiEnabled flag
const dispatchAuthAction = (
  dispatch,
  device,
  crsId,
  showLoading,
  useCotton = getCottonSession(),
) => (
  flagEnabled('replaceMacaroonApiEnabled')
    ? dispatch(dispatchGetUserAuth(crsId, showLoading, useCotton)).payload
    : dispatch(dispatchGetAuth(device, crsId, showLoading, useCotton)).payload
);

/**
 * Attempt to authenticate by dispatching the `dispatchGetAuth` action, with a single
 * automatic retry that first attempts a passive consent flow if the initial attempt fails.
 */
const attemptAuthWithRetry = async (session, dispatch, device, crsId, showLoading) => {
  try {
    return await dispatchAuthAction(dispatch, device, crsId, showLoading);
  } catch (error) {
    await getPassiveAuth();
    try {
      return await dispatchAuthAction(dispatch, device, crsId, showLoading);
    } catch (authError) {
      throw new Error(`authentication via consent (passive passiveAuth retry) failed for session status "${session.status}": ${authError.message}`);
    }
  }
};

/** Fetches macaroon and sets account permissions for new auth flow */
const getMacaroon = (
  device, showLoading = true,
) => async (dispatch, getState) => {
  const { auth: { crsId } = {} } = getState();

  try {
    const response = await dispatch(
      dispatchGetMacaroon(device, crsId, showLoading),
    ).payload;

    if (crsId) {
      return dispatch(dispatchGetMacaroonSuccess(response));
    }

    setGuid(response.guid);

    await dispatch(
      dispatchSetAccountPerms(getAccountPerms(response)),
    );

    const { auth: { accountPerms } } = getState();
    const result = dispatch(dispatchGetMacaroonSuccess(response));
    dispatchPermsErrorIfUnauthorized(dispatch, accountPerms, false);
    return result;
  } catch (error) {
    dispatch(dispatchGetMacaroonFailure(error));
    throw error;
  }
};

/** Existing auth flow (iframe-based) — preserved as fallback */
const getAuthExisting = (device, showLoading = true) => async (dispatch, getState) => {
  const { auth: { crsId } = {} } = getState();
  let session;

  try {
    session = await dispatch(getSession());

    // Calls passive if session status received is not in the skip lists
    if (!SKIP_PASSIVE_AUTH_SESSIONS.includes(session.status)) {
      await getPassiveAuth();
    }
    // If session status is NULL, we would retry for second time
    // This scenario is for the case when user navigates from D2 to Payments app
    // where the session api is always observed to return null in first instance
    if (session.status === 'NULL') {
      session = await dispatch(getSession());
    }
    // After trying for second time, if session status is still NULL or not in the allowed list,
    // we would force auth
    if (!ALLOWED_AUTH_SESSIONS.includes(session.status)) {
      const logEventName = session.status === 'NULL'
        ? 'session_status_null_after_second_attempt'
        : 'session_status_invalid_after_second_attempt';

      kibanaLog(logEventName, {
        crsId,
        sessionStatus: session.status,
      });
      const statusError = new Error(`invalid session status: ${session.status}`);
      statusError.forceAuthentication = true;
      throw statusError;
    }

    const response = await attemptAuthWithRetry(session, dispatch, device, crsId, showLoading);
    dispatch(setAuthStatus(session.status));
    if (crsId) {
      return dispatch(dispatchGetAuthSuccess(response));
    }

    // On initial page load don't validate the account index, just set it.
    // The first time getAuth is called auth.accountIndex should be undefined.
    // On reauth, auth.accountIndex should be set, so we should
    // validate it didn't change in setAccountIndex.
    const { auth: { accountIndex: currentAccountIndex } = {} } = getState();
    const validateAccountIndex = Number.isInteger(currentAccountIndex);

    // Set the CSP Auth header for the selected account once macaroon returns.
    const accountIndex = getAccountIndex(response);
    setAccountIndex(accountIndex, validateAccountIndex);
    dispatch(dispatchSetAccountIndex(accountIndex));
    setCspAccountToken(response, accountIndex);

    setGuid(response.guid);

    await dispatch(dispatchSetAccountPerms(getAccountPerms(response)));

    const { auth: { accountPerms } } = getState();

    const result = dispatch(dispatchGetAuthSuccess(response));
    const isPrimaryOrManager = accountPerms.some(item => ['PRIMARY', 'BILLPAY'].indexOf(item) > -1);
    const isPrimary = accountPerms.includes('PRIMARY');
    if (!isPrimaryOrManager || (isNowPage(window.location.pathname) && !isPrimary)) {
      dispatch(dispatchSetPermsError());
    }
    return result;
  } catch (error) {
    if (error.forceAuthentication) {
      dispatch(dispatchSetForceAuthentication());
      forceAuth();
      throw error;
    }

    if (crsId) {
      localStorage.removeItem('crsId');
    }

    dispatch(dispatchGetAuthFailure(error));
    throw error;
  }
};

/** New auth flow using browser-window redirects instead of iframes */
export const getAuth = (device, showLoading = true) => async (dispatch, getState) => {
  const { isCottonEnabled } = cottonFlagManager;
  if (isCottonEnabled) {
    return getAuthApt(device, showLoading)(dispatch, getState);
  }
  // If Akamai cookie not set, fall back to existing iframe flow
  if (!isTargetAuthEnabled()) {
    return getAuthExisting(device, showLoading)(dispatch, getState);
  }

  try {
    // Check r-hint cookie to determine if user is logged in
    if (!isHintCookieAvailable()) {
      const authError = new Error(
        'hint cookie missing, forcing authentication',
      );
      authError.forceAuthentication = true;
      throw authError;
    }
    // Make session call and determine the session status
    const session = await dispatch(getSession());

    if (ALLOWED_AUTH_SESSIONS.includes(session.status)) {
      const { firebaseEnabled } = config();
      if (firebaseEnabled) {
        await whenFlagsReady();
      }
      const isReplaceMacaroonApiEnabled = flagEnabled('replaceMacaroonApiEnabled');
      // Session valid — fetch macaroon or user based on migration status
      if (isReplaceMacaroonApiEnabled) {
        await getUser(showLoading)(dispatch, getState);
      } else {
        await getMacaroon(device, showLoading)(dispatch, getState);
      }
      dispatch(setAuthStatus(session.status));
      return undefined;
    }

    // Session is null — redirect to passive auth with account selection
    passiveLoginV2();
  } catch (error) {
    if (error.forceAuthentication) {
      dispatch(dispatchSetForceAuthentication());
      forceLoginV2();
      throw error;
    }
    dispatch(dispatchGetAuthFailure(error));
    throw error;
  }
  return undefined;
};

const getAuthApt = (device, showLoading = true) => async (dispatch, getState) => {
  try {
    const hasHint = isHintCookieAvailable();

    kibanaLog('apt_oauth_v1_routing_decision', {
      oauth_enabled: 'true',
      disable_ruby_apis: String(cottonFlagManager.disableRubyApisPCI),
      is_cotton_session: 'true',
      has_hint_cookie: String(hasHint),
    });

    if (!hasHint) {
      kibanaLog('apt_oauth_v1_force_redirect');
      forceLoginV1();
      return null; // Prevent further execution after redirect; flow resumes after re-authentication
    }

    const session = await dispatch(getSession(true));
    const sessionStatus = session?.status;

    kibanaLog('apt_oauth_v1_session_status', {
      session_status: sessionStatus,
      is_allowed_session: String(ALLOWED_AUTH_SESSIONS.includes(sessionStatus)),
    });

    // Once all non-oauth endpoints are migrated to Kotlin,
    // remove the ruby passive auth and session APIs.
    if (!cottonFlagManager.disableRubyApisPCI) {
      try {
        await passiveAuth();
        await dispatch(getSession(false));
      } catch (rubySessionError) {
        kibanaLog('cotton_ruby_session_bootstrap_failed');
      }
    }

    // If session is invalid → passive login
    if (!ALLOWED_AUTH_SESSIONS.includes(sessionStatus)) {
      kibanaLog('apt_oauth_v1_passive_redirect', { session_status: sessionStatus });
      passiveLoginV1();
      return null; // Prevent further execution after redirect; flow resumes after re-auth.
    }

    // Fetch macaroon/user for all allowed session statuses (CONSENT and LOGIN FULL)
    const { firebaseEnabled } = config();
    if (firebaseEnabled) {
      await whenFlagsReady();
    }
    const isReplaceMacaroonApiEnabled = flagEnabled('replaceMacaroonApiEnabled');
    if (isReplaceMacaroonApiEnabled) {
      await getUser(showLoading, true)(dispatch, getState);
    } else {
      await getMacaroon(device, showLoading, true)(dispatch, getState);
    }
    dispatch(setAuthStatus(sessionStatus));
  } catch (error) {
    if (error.forceAuthentication) {
      dispatch(dispatchSetForceAuthentication());
      kibanaLog('apt_oauth_v1_force_redirect');
      forceLoginV1();
      return null;
    }
    dispatch(dispatchGetAuthFailure(error));
    throw error;
  }
  return undefined;
};

export const checkSafeConnections = () => async (dispatch) => {
  const currentPath = window.location.pathname;
  if (!isMobilePage(currentPath) || currentPath === MOBILE_MAINTENANCE_ERROR) {
    return null;
  }

  const isCottonSession = getCottonSession();

  try {
    const response = await dispatch(dispatchCheckSafeConnections(isCottonSession)).payload;
    return dispatch(dispatchCheckSafeConnectionsSuccess(response));
  } catch (error) {
    if (error.status === 423) {
      window.location.href = MOBILE_MAINTENANCE_ERROR;
    }
    return null;
  }
};

export const addRefreshAuthListener = () => (dispatch, getState) => {
  window.addEventListener('refreshAuth', async ({ authPromise }) => {
    const { auth: { crsId } = {}, harness: { device } } = getState();

    if (crsId || !isTargetAuthEnabled()) {
      return authPromise.reject();
    }
    try {
      await dispatch(getAuth(device, false));
      return authPromise.resolve();
    } catch (error) {
      authPromise.reject(error);
      throw error;
    }
  });
};

/** Fetches user via SSM User API and sets account permissions (replaces Macaroon API) */
const getUser = (showLoading = true, useCotton = false) => async (dispatch, getState) => {
  const { auth: { crsId } = {} } = getState();
  try {
    const response = await dispatch(
      dispatchGetUserAuth(crsId, showLoading, useCotton),
    ).payload;

    if (crsId) {
      dispatch(dispatchGetUserSuccess(response));
      return dispatch(dispatchGetUserAuthSuccess());
    }

    setGuid(response.customerGuid);

    await dispatch(
      dispatchSetAccountPerms(getAccountPermsFromUserApi(response)),
    );

    const { auth: { accountPerms } } = getState();
    dispatch(dispatchGetUserSuccess(response));
    const result = dispatch(dispatchGetUserAuthSuccess());
    dispatchPermsErrorIfUnauthorized(dispatch, accountPerms, true);
    return result;
  } catch (error) {
    // This is handling a scenario where user comes back to the payments application in the same tab
    // and session API gives 200 status and user API returns 401 status.
    // The same implementation is observed when Macaroon API used to return 401 status
    // and was handled as part of maw-fetch library to navigate to login page.
    if (error.status === 401 && isTargetAuthEnabled()) {
      error.forceAuthentication = true;
    }
    dispatch(dispatchGetUserAuthFailure(error));
    dispatch(dispatchGetUserFailure(error));
    throw error;
  }
};
